import React, { useState, useEffect, useRef } from "react";
import { Edit2, Plus, User, FileText, Upload, Trash2, Loader2, Check, AlertCircle, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { formatDistanceToNow } from "date-fns";
import { ChatArtifact } from "./ChatArtifact";
import { parseArtifacts, DocumentArtifact } from "@/lib/documentConverter";

interface ProjectSettings {
  id: string;
  system_instructions: string | null;
  memory: string | null;
  model: string | null;
  updated_at: string;
}

interface ProjectFile {
  id: string;
  file_name: string;
  file_type: string | null;
  file_size: number | null;
  created_at: string;
  anthropic_file_id: string | null;
}

interface ChatProjectSettingsPanelProps {
  projectId: string;
}

export const ChatProjectSettingsPanel: React.FC<ChatProjectSettingsPanelProps> = ({ projectId }) => {
  const [settings, setSettings] = useState<ProjectSettings | null>(null);
  const [files, setFiles] = useState<ProjectFile[]>([]);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isLoadingFiles, setIsLoadingFiles] = useState(true);
  const [isEditingMemory, setIsEditingMemory] = useState(false);
  const [isEditingInstructions, setIsEditingInstructions] = useState(false);
  const [editedMemory, setEditedMemory] = useState("");
  const [editedInstructions, setEditedInstructions] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [projectArtifacts, setProjectArtifacts] = useState<DocumentArtifact[]>([]);
  const [isLoadingArtifacts, setIsLoadingArtifacts] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
    fetchFiles();
    fetchProjectArtifacts();
  }, [projectId]);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from("chat_project_settings")
        .select("*")
        .eq("project_id", projectId)
        .single();

      if (error) throw error;
      setSettings(data);
      setEditedMemory(data.memory || "");
      setEditedInstructions(data.system_instructions || "");
    } catch (error: any) {
      console.error("Error fetching settings:", error);
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const fetchFiles = async () => {
    try {
      const { data, error } = await supabase
        .from("chat_project_files")
        .select("id, file_name, file_type, file_size, created_at, anthropic_file_id")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setFiles(data || []);
    } catch (error: any) {
      console.error("Error fetching files:", error);
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const saveMemory = async () => {
    if (!settings) return;
    
    try {
      const { error } = await supabase
        .from("chat_project_settings")
        .update({ memory: editedMemory.trim() || null })
        .eq("id", settings.id);

      if (error) throw error;
      setSettings({ ...settings, memory: editedMemory.trim() || null });
      setIsEditingMemory(false);
      toast({ title: "Memory saved" });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const saveInstructions = async () => {
    if (!settings) return;
    
    try {
      const { error } = await supabase
        .from("chat_project_settings")
        .update({ system_instructions: editedInstructions.trim() || null })
        .eq("id", settings.id);

      if (error) throw error;
      setSettings({ ...settings, system_instructions: editedInstructions.trim() || null });
      setIsEditingInstructions(false);
      toast({ title: "Instructions saved" });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Maximum file size is 10MB",
        variant: "destructive",
      });
      return;
    }

    const allowedTypes = ["text/plain", "text/markdown", "application/pdf", "application/json", "text/csv"];
    if (!allowedTypes.includes(file.type) && !file.name.endsWith(".md")) {
      toast({
        title: "Unsupported file type",
        description: "Please upload a TXT, MD, PDF, JSON, or CSV file",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const storagePath = `${user.id}/${projectId}/${fileName}`;

      // Upload to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from("chat-files")
        .upload(storagePath, file);

      if (uploadError) throw uploadError;

      // Upload to Claude Files API
      let anthropicFileId = null;
      try {
        toast({ title: "Syncing with Claude...", description: "Processing file for AI" });
        
        const { data: claudeResult, error: claudeError } = await supabase.functions.invoke(
          'upload-file-to-claude',
          {
            body: {
              storagePath,
              fileName: file.name,
              fileType: file.type,
            },
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
          }
        );

        if (claudeError) {
          console.error('Claude upload error:', claudeError);
          toast({ 
            title: "Warning", 
            description: "File saved but Claude sync failed. AI may not be able to read this file.",
            variant: "destructive",
          });
        } else if (claudeResult?.file_id) {
          anthropicFileId = claudeResult.file_id;
          console.log('File synced to Claude:', anthropicFileId);
        }
      } catch (claudeErr) {
        console.error('Claude upload failed:', claudeErr);
        // Continue - file is still saved to Supabase
      }

      // Save to database with anthropic_file_id
      const { error: dbError } = await supabase
        .from("chat_project_files")
        .insert({
          project_id: projectId,
          user_id: user.id,
          file_name: file.name,
          file_type: file.type || "text/plain",
          file_size: file.size,
          storage_path: storagePath,
          content_text: null, // No longer needed with Files API
          anthropic_file_id: anthropicFileId,
        });

      if (dbError) throw dbError;

      toast({ 
        title: anthropicFileId ? "File uploaded & synced" : "File uploaded",
        description: anthropicFileId ? "Claude can now read this file" : undefined,
      });
      fetchFiles();
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const deleteFile = async (fileId: string) => {
    try {
      const { error } = await supabase
        .from("chat_project_files")
        .delete()
        .eq("id", fileId);

      if (error) throw error;
      setFiles(files.filter(f => f.id !== fileId));
      toast({ title: "File deleted" });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const fetchProjectArtifacts = async () => {
    try {
      // Get all conversations for this project
      const { data: conversations, error: convError } = await supabase
        .from("chat_conversations")
        .select("id")
        .eq("project_id", projectId);

      if (convError) throw convError;
      if (!conversations || conversations.length === 0) {
        setProjectArtifacts([]);
        return;
      }

      // Get all messages from these conversations
      const conversationIds = conversations.map(c => c.id);
      const { data: messages, error: msgError } = await supabase
        .from("chat_messages")
        .select("content, created_at")
        .in("conversation_id", conversationIds)
        .eq("role", "assistant")
        .order("created_at", { ascending: false });

      if (msgError) throw msgError;

      // Parse artifacts from all messages
      const allArtifacts: DocumentArtifact[] = [];
      (messages || []).forEach(msg => {
        const { artifacts } = parseArtifacts(msg.content);
        allArtifacts.push(...artifacts);
      });

      setProjectArtifacts(allArtifacts);
    } catch (error: any) {
      console.error("Error fetching artifacts:", error);
    } finally {
      setIsLoadingArtifacts(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Memory Section */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Memory</h3>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs gap-1">
              <User className="h-3 w-3" />
              Only you
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setIsEditingMemory(!isEditingMemory)}
            >
              <Edit2 className="h-4 w-4 text-muted-foreground" />
            </Button>
          </div>
        </div>
        
        {isEditingMemory ? (
          <div className="space-y-2">
            <Textarea
              value={editedMemory}
              onChange={(e) => setEditedMemory(e.target.value)}
              placeholder="Add facts, preferences, or context Claude should remember..."
              className="min-h-[100px] text-sm resize-none"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={saveMemory}>Save</Button>
              <Button size="sm" variant="ghost" onClick={() => {
                setIsEditingMemory(false);
                setEditedMemory(settings?.memory || "");
              }}>Cancel</Button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-muted-foreground line-clamp-3">
              {settings?.memory || "No memory set. Click edit to add context Claude should remember."}
            </p>
            {settings?.updated_at && settings?.memory && (
              <p className="text-xs text-muted-foreground mt-2">
                Last updated {formatDistanceToNow(new Date(settings.updated_at), { addSuffix: true })}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Instructions Section */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Instructions</h3>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setIsEditingInstructions(!isEditingInstructions)}
          >
            <Edit2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
        
        {isEditingInstructions ? (
          <div className="space-y-2">
            <Textarea
              value={editedInstructions}
              onChange={(e) => setEditedInstructions(e.target.value)}
              placeholder="E.g., 'Always respond in Norwegian', 'Be concise', 'Format code in TypeScript'..."
              className="min-h-[100px] text-sm resize-none"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={saveInstructions}>Save</Button>
              <Button size="sm" variant="ghost" onClick={() => {
                setIsEditingInstructions(false);
                setEditedInstructions(settings?.system_instructions || "");
              }}>Cancel</Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground line-clamp-3">
            {settings?.system_instructions || "No instructions set. Click edit to add custom instructions."}
          </p>
        )}
      </div>

      {/* Files Section */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Files</h3>
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.md,.pdf,.json,.csv"
            onChange={handleUpload}
            className="hidden"
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 text-muted-foreground" />
            )}
          </Button>
        </div>
        
        {isLoadingFiles ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : files.length === 0 ? (
          <div 
            className="flex flex-col items-center justify-center py-8 px-4 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 bg-muted rounded-lg">
                <FileText className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="p-2 bg-muted rounded-lg">
                <Plus className="h-5 w-5 text-muted-foreground" />
              </div>
              <div className="p-2 bg-muted rounded-lg">
                <FileText className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Add PDFs, documents, or other text to reference in this project.
            </p>
          </div>
        ) : (
          <ScrollArea className="max-h-[200px]">
            <div className="space-y-2">
              {files.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center justify-between p-2 rounded-lg bg-muted/50 group"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm font-medium truncate">{file.file_name}</p>
                        {file.anthropic_file_id ? (
                          <span title="Synced with Claude">
                            <Check className="h-3.5 w-3.5 text-green-500 shrink-0" />
                          </span>
                        ) : (
                          <span title="Not synced - AI cannot read this file">
                            <AlertCircle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(file.file_size)}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 shrink-0"
                    onClick={() => deleteFile(file.id)}
                  >
                    <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>

      {/* Downloads Section */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold flex items-center gap-2">
            <Download className="h-4 w-4" />
            Downloads
          </h3>
          <Badge variant="outline" className="text-xs">
            {projectArtifacts.length} file{projectArtifacts.length !== 1 ? "s" : ""}
          </Badge>
        </div>
        
        {isLoadingArtifacts ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : projectArtifacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 px-4 border-2 border-dashed border-border rounded-lg">
            <Download className="h-8 w-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground text-center">
              No downloads yet
            </p>
            <p className="text-xs text-muted-foreground text-center mt-1">
              Ask Claude to create documents, spreadsheets, or PDFs
            </p>
          </div>
        ) : (
          <ScrollArea className="max-h-[200px]">
            <div className="space-y-2">
              {projectArtifacts.map((artifact, idx) => (
                <ChatArtifact key={idx} artifact={artifact} />
              ))}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
};